import { last } from 'lodash';
import mime from 'mime';

import { getTokens } from '../../firebase/authentication';

export const FileSizeImage = '2MB';
export const FileSizeDocument = '5MB';

export const FileTypeImage = [
  'image/jpg',
  'image/jpeg',
  'image/png',
  'image/gif',
];

export const FileTypeWord = [
  'doc',
  'docx',
  'pages',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/pages',
  'application/vnd.apple.pages',
];

export const FileTypeExcel = [
  'xls',
  'xlsx',
  'numbers',
  'application/msexcel',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/numbers',
  'application/vnd.apple.numbers',
];

export const FileTypePdf = ['pdf', 'application/pdf'];

export const FileTypeGpx = [
  '.gpx',
  'application/gpx',
  'application/gpx+xml',
  'application/octet-stream',
  'bin',
];

export type FileItemData = {
  name: string;
  size: number;
  base64: string;
};

export type UploadFileItemData = {
  fileName?: string;
  originalFileName?: string;
  size?: number;
  base64?: string;
  thumbNail?: string;
};

export const formatMimeType = (file: string | null | undefined): string => {
  if (file) {
    const type = mime.getType(file);

    if (type) {
      return mime.getExtension(type) ?? 'unknown';
    }
  }

  return 'unknown';
};

export const formatFileSize = (size: number | string, type: string): string => {
  if (type === 'MB') {
    return `${(parseFloat(size.toString()) / 1024 ** 2).toFixed(2)}Mbs`;
  }

  return '0';
};

export const getThumbnail = (
  file: string | null | undefined
): string | null => {
  if (file) {
    const type = (file.split(',').pop() ?? file).substring(0, 3);

    if (['/9j', 'iVB', 'Qk0', 'SUk', 'R0l'].includes(type)) {
      return file;
    }
  }

  return null;
};

export const calculateFileSize = (size: string) => {
  if (size.search('KB') !== -1) {
    return parseInt(size) * 1024;
  }

  if (size.search('MB') !== -1) {
    return parseInt(size) * 1024 * 1024;
  }

  return 0;
};

export const isValidFileType = (
  fileName: string | null | undefined,
  fileTypes: string[]
) =>
  fileTypes
    .map(option => last(option.split('/')))
    .includes(formatMimeType(fileName));

export const isValidFileSize = (
  fileSize: number | null | undefined,
  maxFileSize: string
) => (fileSize ?? 0) <= calculateFileSize(maxFileSize);

/*
const readFileAsDataURL = (file: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () =>
      reader.result ? resolve(reader.result.toString()) : reject();
    reader.onerror = reject;
    reader.readAsDataURL(file);
  }); */

/*
export const getFileDataFromFileMessageFile = async (
  fileMessage: UploadFile | null | undefined
): Promise<FileItemData> => {
  if (!fileMessage) throw new Error('No fileMessage provided.');
  const tokens = await getTokens();
  const pinCodeId = localStorage.getItem('pincodeid');

  const file = fileMessage.file
    ? await (
        await fetch(fileMessage.file, {
          headers: new Headers({
            Authorization: `Bearer ${tokens.token}`,
            PinCodeId: pinCodeId ?? '',
          }),
        })
      ).blob()
    : null;

  if (!file) throw new Error(`No file to read ${fileMessage.originalFileName}`);

  const base64 = await readFileAsDataURL(file);
  return {
    name: fileMessage.originalFileName ?? '',
    size: atob(base64.substring(base64.indexOf(',') + 1)).length,
    base64,
  };
}; */

export const getUploadFileDataFromFile = async (
  file: File | null | undefined
): Promise<UploadFileItemData> =>
  new Promise((resolve, reject) => {
    if (file) {
      const reader = new FileReader();

      reader.onload = () => {
        if (reader.result) {
          resolve({
            fileName: file.name,
            originalFileName: file.name,
            size: file.size,
            base64: reader.result.toString(),
          } as unknown as UploadFileItemData);
        }

        reject();
      };

      reader.onerror = reject;

      reader.readAsDataURL(file as Blob);
    } else {
      reject();
    }
  });

export const getFileDataFromFile = async (
  file: File | null | undefined
): Promise<FileItemData> =>
  new Promise((resolve, reject) => {
    if (file) {
      const reader = new FileReader();

      reader.onload = () => {
        if (reader.result) {
          resolve({
            name: file.name,
            size: file.size,
            base64: reader.result.toString(),
          } as FileItemData);
        }

        reject();
      };

      reader.onerror = reject;

      reader.readAsDataURL(file as Blob);
    } else {
      reject();
    }
  });

export const getFileDataFromUrl = async (
  url: string | null | undefined
): Promise<FileItemData> => {
  const file = url ? await (await fetch(url)).blob() : null;

  return new Promise((resolve, reject) => {
    if (file) {
      const reader = new FileReader();

      reader.onload = () => {
        if (reader.result) {
          resolve({
            name: url ? url.replace(/^.*[\\/]/, '') : '',
            size: atob(
              reader.result
                .toString()
                .substring(reader.result.toString().indexOf(',') + 1)
            ).length,
            base64: reader.result.toString(),
          } as FileItemData);
        }

        reject();
      };

      reader.onerror = reject;

      reader.readAsDataURL(file);
    } else {
      reject();
    }
  });
};

export const getBase64FromServer = async (url: string): Promise<string> => {
  const tokens = await getTokens();
  const pinCodeId = localStorage.getItem('pincodeid');
  const data = await fetch(url, {
    headers: new Headers({
      Authorization: `Bearer ${tokens.token}`,
      PinCodeId: pinCodeId ?? '',
    }),
  });

  const blob = await data.blob();

  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onloadend = () => {
      if (reader.result) {
        resolve(reader.result.toString());
      }

      reject();
    };

    reader.onerror = reject;

    reader.readAsDataURL(blob);
  });
};

export const onHandleDownloadFile = async (
  url: string,
  name: string | null | undefined = undefined
) => {
  const tokens = await getTokens();
  const pinCodeId = localStorage.getItem('pincodeid');
  const data = await fetch(url, {
    headers: new Headers({
      Authorization: `Bearer ${tokens.token}`,
      PinCodeId: pinCodeId ?? '',
    }),
  });

  const blob = await data.blob();

  const link = document.createElement('a');

  if (url && link.download !== undefined) {
    link.setAttribute('href', URL.createObjectURL(blob));
    link.setAttribute('download', name ?? url.replace(/^.*[\\/]/, ''));

    link.style.display = 'none';

    document.body.appendChild(link);

    link.click();

    document.body.removeChild(link);
  }
};
